import React from 'react';
import Icons from '@/const/icons';
import { useNotification } from '@/hooks';
import { ApprovePhase, StepStatusCase } from '@/interfaces';
import { CaseInitiationCard } from './CaseInitiationCard';
import { OptRequestDocument } from './OptRequestDocument';
import { CaseContext } from '@/context/CaseContext';
import {
	UploadFileCard,
	UploadPDFModal,
} from '@/components/modals/UploadPDFModal';
import { Button } from '@/components/common';
import { caseApplicantService } from '@/api/caseApplicant';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
	auditService,
	casesLawyerService,
	GET_CASE_STEPS_STATUS,
	GET_LAWYER_CASE_DETAILS,
	GET_OPT_HISTORY,
} from '@/api';

interface OptRequestFormProps {
	caseId: string;
	phaseId: number;
	stepCase?: StepStatusCase;
}
const OptRequestForm: React.FC<OptRequestFormProps> = ({
	caseId,
	phaseId,
	stepCase,
}) => {
	const queryClient = useQueryClient();
	const { toastSuccess, toastError } = useNotification();
	const { isCaseReadOnly } = React.useContext(CaseContext);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);

	const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
		React.useState<boolean>(false);
	const [pdfFile, setPdfFile] = React.useState<File | undefined>();
	const [isThereOptUploaded, setIsThereOptUploaded] =
		React.useState<boolean>(false);

	const {
		data: optHistory,
		isLoading: isLoadingOptHistory,
		refetch,
	} = useQuery({
		queryKey: [GET_OPT_HISTORY, caseId],
		queryFn: () => caseApplicantService.getOPTHistory(caseId || ''),
		enabled: !!caseId,
	});

	// upload document
	const handleConfirm = async () => {
		if (!pdfFile) return;
		try {
			await caseApplicantService.postUploadOPT(caseId, pdfFile);
			refetch();
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setIsUploadDocumentModalOpen(false);
		}
	};

	const handleSend = async () => {
		setIsLoading(true);
		try {
			await casesLawyerService.sendOptDocument(caseId);
			await handleApprove();
			toastSuccess('The Opt Out has been successfully sent');
			setIsUploadDocumentModalOpen(false);
		} catch (error) {
			toastError('An error has occurred. Please try again.');
		} finally {
			setIsLoading(false);
			setPdfFile(undefined);
		}
	};

	const handleApprove = async () => {
		try {
			if (caseId && stepCase) {
				const data: ApprovePhase = {
					case_id: caseId as string,
					lawyer_stepper_id: phaseId,
					stepper_form_id: stepCase.step_id,
				};

				await auditService.postApprovePhase(data);
				refetch();
				queryClient.refetchQueries({
					queryKey: [GET_CASE_STEPS_STATUS, caseId],
				});
				queryClient.refetchQueries({
					queryKey: [GET_LAWYER_CASE_DETAILS, caseId],
				});
			}
		} catch (error) {
			toastError(
				'An error occurred while confirming approval. Please try again.'
			);
		}
	};

	React.useEffect(() => {
		if (optHistory && optHistory?.data.length > 0) {
			setIsThereOptUploaded(!optHistory?.data[0].is_sent);
		}
	}, [optHistory]);

	const documentName = isThereOptUploaded
		? optHistory?.data[0].document_name ?? ''
		: pdfFile?.name ?? '';

	const optHistoryFiltered = optHistory?.data.filter((item) => item.is_sent);

	return (
		<>
			<div className="space-y-8 divide-y divide-grey-10">
				<div className="space-y-8">
					<CaseInitiationCard
						title="Upload Opt Out Request Document"
						subtitle="Click to open the upload window. Please ensure you have reviewed the client list before submitting the opt out."
						titleCard="Upload Opt Out"
						subtitleCard=""
						buttonLabel="Upload Opt Out"
						onClick={() => setIsUploadDocumentModalOpen(true)}
						buttonIcon={Icons.documentUpload}
						buttonDisabled={isCaseReadOnly}
						buttonCustom={
							(pdfFile || isThereOptUploaded) && (
								<div className="bg-white p-2 rounded-lg flex items-center gap-2">
									<UploadFileCard
										pdfName={documentName}
										setPdfFile={setPdfFile}
										removeIconRemove={Icons.trash}
										classNameIcon="text-main-red"
										handleRemove={() => {
											setPdfFile(undefined);
											setIsThereOptUploaded(false);
										}}
									/>
									<Button
										label="Send"
										decoration="primary"
										size="fit"
										onClick={handleSend}
										className="h-12"
										loading={isLoading}
									/>
								</div>
							)
						}
					/>
					<OptRequestDocument
						isLoading={isLoadingOptHistory}
						optHistory={optHistoryFiltered || []}
					/>
				</div>
			</div>
			<UploadPDFModal
				isOpen={isUploadDocumentModalOpen}
				setIsModalOpen={setIsUploadDocumentModalOpen}
				setPdfFile={setPdfFile}
				pdfFile={pdfFile}
				handleConfirmButton={handleConfirm}
			/>
		</>
	);
};

export default OptRequestForm;

// import React from 'react';
// import Icons from '@/const/icons';
// import { useNotification } from '@/hooks';
// import useModalPreviewPage from '@/hooks/useModalPreviewPage';
// import OptRequestPreview from './OptRequestPreview';
// import { UploadPDFModal } from '@/components/modals/UploadPDFModal';
// import { ApprovePhase, StepStatusCase } from '@/interfaces';
// import { useQueryClient } from '@tanstack/react-query';
// import {
// 	auditService,
// 	GET_CASE_STEPS_STATUS,
// 	GET_LAWYER_CASE_DETAILS,
// } from '@/api';
// import { CaseInitiationCard } from './CaseInitiationCard';
// import { OptRequestDocument } from './OptRequestDocument';

// interface OptRequestFormProps {
// 	caseId: string;
// 	phaseId: number;
// 	stepCase?: StepStatusCase;
// 	caseTitle?: string;
// }
// const OptRequestForm: React.FC<OptRequestFormProps> = ({
// 	caseId,
// 	phaseId,
// 	stepCase,
// 	// caseTitle,
// }) => {
// 	// const PAGE_SIZE = 5;
// 	const queryClient = useQueryClient();
// 	const { toastSuccess, toastError } = useNotification();
// 	// const { isCaseReadOnly } = React.useContext(CaseContext);
// 	const { ModalPreviewPage, hidePreviewPage } = useModalPreviewPage();
// 	const [isUploadDocumentModalOpen, setIsUploadDocumentModalOpen] =
// 		React.useState<boolean>(false);
// 	const [pdfFile, setPdfFile] = React.useState<File | undefined>();
// 	// const [fileError, setFileError] = React.useState<boolean>(false);
// 	// const [isSaving, setIsSaving] = React.useState<boolean>(false);
// 	// const [isEdited, setIsEdited] = React.useState<boolean>(false);
// 	// const [isOptRequired, setIsOptRequired] = React.useState<boolean>(false);
// 	// const [renderedFileInfo, setRenderedFileInfo] = React.useState<
// 	// 	OptDocument | undefined
// 	// >();

// 	// const [signatureList, setSignatureList] = React.useState<SignatureData[]>([]);
// 	// const { register } = useForm({
// 	// 	mode: 'onChange',
// 	// });
// 	const isStepApproved = stepCase?.status === 'approved';
// 	// const isStepCompleted = stepCase?.status === 'completed';

// 	// const { data: savedOptConfig, refetch: refetchSavedOptConfig } = useQuery({
// 	// 	queryKey: [GET_SAVED_OPT_CONFIG, caseId],
// 	// 	queryFn: () => casesService.getSavedOptConfig(caseId),
// 	// 	enabled: !!caseId,
// 	// });

// 	// const fetchData = async ({
// 	// 	pageParam = 1,
// 	// }: {
// 	// 	pageParam?: number;
// 	// }): Promise<any> => {
// 	// 	const response = await casesService.getAcceptedOptSignatures(
// 	// 		caseId,
// 	// 		pageParam,
// 	// 		PAGE_SIZE
// 	// 	);
// 	// 	return response.data;
// 	// };
// 	// const {
// 	// 	data: optSignaturesData,
// 	// 	// fetchNextPage,
// 	// 	// hasNextPage,
// 	// 	// isFetching: isLoadingMoreSignatures,
// 	// } = useInfiniteQuery({
// 	// 	initialData: undefined,
// 	// 	initialPageParam: 1,
// 	// 	queryKey: [GET_OPT_SIGNATURES, caseId],
// 	// 	queryFn: fetchData,
// 	// 	getNextPageParam(lastPage) {
// 	// 		return lastPage.page < lastPage.totalPages
// 	// 			? lastPage.page + 1
// 	// 			: undefined;
// 	// 	},
// 	// 	refetchOnWindowFocus: false,
// 	// 	enabled: !!caseId,
// 	// });

// 	// React.useEffect(() => {
// 	// 	if (
// 	// 		(isOptRequired !== savedOptConfig?.data.is_required &&
// 	// 			savedOptConfig?.data.is_required !== null) ||
// 	// 		(savedOptConfig?.data.is_required === null && !!isOptRequired) ||
// 	// 		!!pdfFile
// 	// 	) {
// 	// 		setIsEdited(true);
// 	// 	} else {
// 	// 		setIsEdited(false);
// 	// 	}
// 	// }, [isOptRequired, pdfFile]);

// 	// React.useEffect(() => {
// 	// 	if (optSignaturesData) {
// 	// 		setSignatureList(() => {
// 	// 			return optSignaturesData.pages.flatMap((page: any) =>
// 	// 				page.items.map((item: any) => ({
// 	// 					name: item.name,
// 	// 					signature: item.signature,
// 	// 					date: item.date,
// 	// 				}))
// 	// 			);
// 	// 		});
// 	// 	} else {
// 	// 		setSignatureList([]);
// 	// 	}
// 	// }, [optSignaturesData]);

// 	// React.useEffect(() => {
// 	// 	if (pdfFile) {
// 	// 		// setFileError(false);
// 	// 		setRenderedFileInfo({
// 	// 			case_id: caseId,
// 	// 			document_name: pdfFile.name,
// 	// 			document_type: 'Opt',
// 	// 			file_path: '',
// 	// 			sent_at: '',
// 	// 			updated_at: '',
// 	// 			created_at: '',
// 	// 			id: 0,
// 	// 		});
// 	// 	}
// 	// }, [pdfFile]);

// 	// React.useEffect(() => {
// 	// 	if (savedOptConfig) {
// 	// 		setIsOptRequired(savedOptConfig.data.is_required);
// 	// 		setRenderedFileInfo(savedOptConfig.data.optDocument[0]);
// 	// 	}
// 	// }, [savedOptConfig]);

// 	// const handleComplete = async () => {
// 	// 	if ((pdfFile || renderedFileInfo) && isOptRequired) {
// 	// 		try {
// 	// 			// setIsSaving(true);
// 	// 			await handleUploadDocument();
// 	// 			await casesService.completeOptConfig(caseId, isOptRequired);
// 	// 			refetchSavedOptConfig();
// 	// 			toastSuccess('The case section has been successfully completed.');
// 	// 			queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS] });
// 	// 		} catch (error) {
// 	// 			toastError(
// 	// 				'An error occurred while confirming completion. Please try again.'
// 	// 			);
// 	// 		} finally {
// 	// 			// setFileError(false);
// 	// 			// setIsSaving(false);
// 	// 			// setIsEdited(false);
// 	// 		}
// 	// 	}
// 	// 	if (!pdfFile && !renderedFileInfo && isOptRequired) {
// 	// 		setFileError(true);
// 	// 		return;
// 	// 	}
// 	// };

// 	// const handleSave = async () => {
// 	// 	try {
// 	// 		setIsSaving(true);
// 	// 		await handleUploadDocument();
// 	// 		await casesService.savedOptConfig(caseId, isOptRequired);
// 	// 		refetchSavedOptConfig();
// 	// 		toastSuccess('The case section has been successfully saved.');
// 	// 		queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS] });
// 	// 	} catch (error) {
// 	// 		toastError(
// 	// 			'An error occurred while saving the case section. Please try again.'
// 	// 		);
// 	// 	} finally {
// 	// 		setIsSaving(false);
// 	// 		setIsEdited(false);
// 	// 	}
// 	// };

// 	const handleApprove = async () => {
// 		try {
// 			if (caseId && stepCase) {
// 				const data: ApprovePhase = {
// 					case_id: caseId as string,
// 					lawyer_stepper_id: phaseId,
// 					stepper_form_id: stepCase.step_id,
// 				};

// 				await auditService.postApprovePhase(data);
// 				toastSuccess('The case section has been successfully approved.');
// 				hidePreviewPage();

// 				queryClient.refetchQueries({ queryKey: [GET_CASE_STEPS_STATUS] });
// 				queryClient.refetchQueries({ queryKey: [GET_LAWYER_CASE_DETAILS] });
// 			}
// 		} catch (error) {
// 			toastError(
// 				'An error occurred while confirming approval. Please try again.'
// 			);
// 		}
// 	};
// 	// const handleUploadDocument = async () => {
// 	// 	try {
// 	// 		const preDocumentId = savedOptConfig?.data.optDocument[0]
// 	// 			? savedOptConfig?.data.optDocument[0]?.id
// 	// 			: undefined;
// 	// 		if (preDocumentId === renderedFileInfo?.id) return; // this is to make sure it does not delete the current file if it was not changed
// 	// 		if (preDocumentId || pdfFile) {
// 	// 			await casesService.uploadCaseDocument(
// 	// 				caseId,
// 	// 				'Opt',
// 	// 				preDocumentId,
// 	// 				pdfFile
// 	// 			);
// 	// 		}
// 	// 	} catch (error) {
// 	// 		toastError('Error: The file could not be uploaded. Please try again.');
// 	// 	}
// 	// };

// 	// const handleDownload = async () => {
// 	// 	if (pdfFile) {
// 	// 		await downloadBlobPDF(pdfFile);
// 	// 		return;
// 	// 	}
// 	// 	if (renderedFileInfo) {
// 	// 		await downloadPDFByUrl(
// 	// 			renderedFileInfo.file_path,
// 	// 			renderedFileInfo.document_name
// 	// 		);
// 	// 	}
// 	// };

// 	return (
// 		<>
// 			<div className="space-y-8 divide-y divide-grey-10">
// 			{/* <div className="mt-1">
// 				<InputCheck
// 					disabled={isCaseReadOnly}
// 					register={register}
// 					name="optOutRequestRequired"
// 					className="!gap-4"
// 					checked={isOptRequired}
// 					onChangeCustom={() => {
// 						setIsOptRequired((pre) => !pre);
// 					}}
// 				>
// 					<Typography type="regular-16">Opt Out Request Required</Typography>
// 				</InputCheck>

// 				<div className="mt-12">
// 					<Typography type="regular-16" className="mb-2">
// 						Opt Out Request Document
// 					</Typography>
// 					<div className="px-6  pt-6 pb-14 rounded-3xl border border-opacity-primary xl:w-[454px] mb-12">
// 						<Typography type="regular-16" className="font-semibold mb-6">
// 							Upload Document
// 						</Typography>
// 						{!renderedFileInfo && (
// 							<Button
// 								disabled={isCaseReadOnly}
// 								label="Upload Document"
// 								icon={Icons.documentUpload}
// 								iconLeft
// 								size="medium"
// 								className="!py-1 !px-[35px]  !border-green-base !h-[30px] mb-2"
// 								classNameButton="!w-4 !h-4"
// 								onClick={() => setIsUploadDocumentModalOpen(true)}
// 							/>
// 						)}
// 						{renderedFileInfo && (
// 							<div className="flex items-start gap-2">
// 								<LabelFileUploaded
// 									icon={Icons.documentDownload}
// 									label={renderedFileInfo?.document_name + '.pdf'}
// 									onClick={handleDownload}
// 									iconClassName="text-main-red"
// 								/>
// 								<button className="mt-2">
// 									<Icon
// 										src={Icons.trash}
// 										width={16}
// 										height={16}
// 										className="!text-main-red"
// 										onClick={() => {
// 											if (!isCaseReadOnly) {
// 												setRenderedFileInfo(undefined);
// 												setPdfFile(undefined);
// 											}
// 										}}
// 									/>
// 								</button>
// 							</div>
// 						)}
// 						{fileError && (
// 							<div className="flex gap-2">
// 								<Icon
// 									src={Icons.exclamationCircle}
// 									width={16}
// 									height={16}
// 									className="!text-main-red"
// 								/>
// 								<Typography type="regular-12" className="text-main-red">
// 									Please upload the required file.
// 								</Typography>
// 							</div>
// 						)}
// 					</div>
// 					<div>
// 						<Typography type="regular-16" className="mb-2">
// 							Signed Opt Out Request
// 						</Typography>

// 						<SignaturesContainer
// 							signatures={signatureList}
// 							hasNextPage={hasNextPage}
// 							fetchNextPage={fetchNextPage}
// 							isLoadingMoreSignatures={isLoadingMoreSignatures}
// 							caseId={caseId}
// 							stepCase={stepCase}
// 							caseTitle={caseTitle}
// 						/>
// 					</div>
// 					<div className="mt-8">
// 						<ButtonsForm
// 							onClickComplete={handleComplete}
// 							onClickSave={handleSave}
// 							onClickPreview={showPreviewPage}
// 							isPreviewDisabled={
// 								(!isStepCompleted && !isStepApproved) || isEdited
// 							}
// 							isSaveDisabled={!isEdited || isCaseReadOnly}
// 							isCompleteDisabled={
// 								isSaving || isStepApproved || isStepCompleted || isCaseReadOnly
// 							}
// 							isCompleted={isStepCompleted || isStepApproved}
// 							completeButtonClassName={
// 								(!pdfFile && !renderedFileInfo) || !isOptRequired
// 									? '!opacity-40 !cursor-not-allowed'
// 									: ''
// 							}
// 						/>
// 					</div>
// 				</div>
// 			</div> */}
// 			<UploadPDFModal
// 				isOpen={isUploadDocumentModalOpen}
// 				setIsModalOpen={setIsUploadDocumentModalOpen}
// 				setPdfFile={setPdfFile}
// 				pdfFile={pdfFile}
// 			/>

// 			<ModalPreviewPage
// 				handleClickApprove={handleApprove}
// 				handleClickBack={hidePreviewPage}
// 				handleClickEdit={hidePreviewPage}
// 				title={isStepApproved ? 'This is your preview page.' : undefined}
// 				hideApproveButton={isStepApproved}
// 				hideEditButton={isStepApproved}
// 			>
// 				<OptRequestPreview />
// 			</ModalPreviewPage>
// 		</>
// 	);
// };

// export default OptRequestForm;
